import { mountChrome } from './chrome.js?v=1789681238';
import { catalog, renderGrid, wireQuickAdd, CONCERN_LABEL, esc, effPrice, searchScore } from './catalog.js?v=1789571604';
import { initMotion } from './motion.js?v=1789571604';

mountChrome();
wireQuickAdd();

/* alternating row tints - landing page stays uniform, shop rows rotate */
const ROW_TINTS = ['#EFEAE1', '#E3E8DE', '#F3E3DE', '#E9E4D6', '#E4E7EA'];

const state = {
  brand: new Set(), cat: new Set(), con: new Set(),
  min: null, max: null, inStock: false, best: false, new: false, q: '',
  sort: 'featured',
};

const $ = s => document.querySelector(s);
const grid = $('[data-grid]');
/* renderGrid appends in batches now, so row tints have to be recomputed each time a
   batch lands or everything past the first screenful renders untinted. */
if (grid) grid.addEventListener('grid:rendered', () => requestAnimationFrame(tintRows));
let ALL = [];

function readURL() {
  const u = new URLSearchParams(location.search);
  ['brand', 'cat', 'con'].forEach(k => u.getAll(k).forEach(v => v.split(',').forEach(x => x && state[k].add(x))));
  if (u.get('best')) state.best = true;
  if (u.get('new')) state.new = true;
  if (u.get('q')) state.q = u.get('q');
  const s = u.get('sort');
  if (s && Object.prototype.hasOwnProperty.call(SORTS, s)) state.sort = s;
}
function writeURL() {
  const u = new URLSearchParams();
  ['brand', 'cat', 'con'].forEach(k => state[k].size && u.set(k, [...state[k]].join(',')));
  if (state.best) u.set('best', '1');
  if (state.new) u.set('new', '1');
  if (state.q) u.set('q', state.q);
  if (state.sort !== 'featured') u.set('sort', state.sort);
  history.replaceState(null, '', u.toString() ? `?${u}` : location.pathname);
}

const matches = p =>
  (!state.brand.size || state.brand.has(p.b)) &&
  (!state.cat.size || p.cat.some(c => state.cat.has(c))) &&
  (!state.con.size || p.con.some(c => state.con.has(c))) &&
  (state.min == null || effPrice(p) >= state.min) &&
  (state.max == null || effPrice(p) <= state.max) &&
  (!state.inStock || p.av) &&
  (!state.best || p.best) &&
  (!state.new || p.new) &&
  (!state.q || searchScore(p, state.q) > 0);

const SORTS = Object.assign(Object.create(null), {
  featured: (a, b) => (b.best - a.best) || (b.av - a.av) || a.b.localeCompare(b.b),
  'price-asc': (a, b) => effPrice(a) - effPrice(b),
  'price-desc': (a, b) => effPrice(b) - effPrice(a),
  name: (a, b) => `${a.b} ${a.t}`.localeCompare(`${b.b} ${b.t}`, undefined, { numeric: true, sensitivity: 'base' }),
  brand: (a, b) => a.b.localeCompare(b.b) || a.t.localeCompare(b.t),
});

/* count for a facet value, respecting every OTHER active facet */
function countFor(kind, value) {
  const saved = new Set(state[kind]);
  state[kind] = new Set([value]);
  const n = ALL.filter(matches).length;
  state[kind] = saved;
  return n;
}

/* Same idea for the Availability rows, which are booleans rather than Sets so they
   cannot go through countFor. They used to print a site-wide ALL.filter(...) total,
   so a shopper who had narrowed to one concern still saw "Best sellers 27" and got
   a much smaller grid on clicking it. Every other facet already respects the
   active filters; these three now do too. */
function countToggle(key) {
  const saved = state[key];
  state[key] = true;
  const n = ALL.filter(matches).length;
  state[key] = saved;
  return n;
}

function railHTML() {
  const brands = [...new Set(ALL.map(p => p.b))].sort();
  const cats = [...new Set(ALL.flatMap(p => p.cat))].sort();
  const cons = Object.keys(CONCERN_LABEL);
  const group = (key, title, values, label = v => v) => `
    <div class="filter-group" data-group="${key}">
      <button class="filter-group__title" aria-expanded="true">${title}
        <svg viewBox="0 0 24 24"><path d="m6 9 6 6 6-6"/></svg></button>
      <div class="filter-group__body"><ul>
        ${values.map(v => `<li><label>
          <input type="checkbox" data-facet="${key}" value="${v}" ${state[key].has(v) ? 'checked' : ''}>
          <span>${label(v)}</span><span class="count">${countFor(key, v)}</span></label></li>`).join('')}
      </ul></div>
    </div>`;
  return `
    <div class="filter-sheet-head">
      <h2>Filter</h2>
      <button class="icon-btn" data-filter-close aria-label="Close filters">✕</button>
    </div>
    ${group('con', 'Skin Concern', cons, v => CONCERN_LABEL[v])}
    ${group('brand', 'Brand', brands)}
    ${group('cat', 'Category', cats)}
    <div class="filter-group" data-group="price">
      <button class="filter-group__title" aria-expanded="true">Price
        <svg viewBox="0 0 24 24"><path d="m6 9 6 6 6-6"/></svg></button>
      <div class="filter-group__body"><div>
        <div class="price-row">
          <input type="number" inputmode="numeric" placeholder="Min" data-price="min" value="${state.min ?? ''}" aria-label="Minimum price">
          <span>to</span>
          <input type="number" inputmode="numeric" placeholder="Max" data-price="max" value="${state.max ?? ''}" aria-label="Maximum price">
        </div>
      </div></div>
    </div>
    <div class="filter-group" data-group="availability">
      <button class="filter-group__title" aria-expanded="true">Availability
        <svg viewBox="0 0 24 24"><path d="m6 9 6 6 6-6"/></svg></button>
      <div class="filter-group__body"><ul>
        <li><label><input type="checkbox" data-toggle="inStock" ${state.inStock ? 'checked' : ''}><span>In stock only</span>
          <span class="count">${countToggle('inStock')}</span></label></li>
        <li><label><input type="checkbox" data-toggle="best" ${state.best ? 'checked' : ''}><span>Best sellers</span>
          <span class="count">${countToggle('best')}</span></label></li>
        <li><label><input type="checkbox" data-toggle="new" ${state.new ? 'checked' : ''}><span>New arrivals</span>
          <span class="count">${countToggle('new')}</span></label></li>
      </ul></div>
    </div>
    <button class="btn btn--primary btn--full filter-apply" data-filter-apply>Show ${ALL.filter(matches).length} product${ALL.filter(matches).length === 1 ? '' : 's'}</button>`;
}

function chipsHTML() {
  const chips = [];
  const add = (kind, v, label) => chips.push(
    `<span class="filter-chip">${esc(label)}<button data-remove="${kind}" data-value="${esc(v)}" aria-label="Remove ${esc(label)} filter">✕</button></span>`);
  state.con.forEach(v => add('con', v, CONCERN_LABEL[v] || v));
  state.brand.forEach(v => add('brand', v, v));
  state.cat.forEach(v => add('cat', v, v));
  if (state.min != null || state.max != null) add('price', 'price', `$${state.min ?? 0}–$${state.max ?? '∞'}`);
  if (state.inStock) add('toggle', 'inStock', 'In stock');
  if (state.best) add('toggle', 'best', 'Best sellers');
  if (state.new) add('toggle', 'new', 'New arrivals');
  if (state.q) add('q', 'q', `“${state.q}”`);
  return chips.length ? chips.join('') + '<button class="clear-all" data-clear-all>Clear all</button>' : '';
}

/* paint one tint per visual row, recalculated whenever the grid reflows */
function tintRows() {
  const cards = [...grid.querySelectorAll('.product-card')];
  let row = -1, lastTop = null;
  cards.forEach(c => {
    const top = Math.round(c.offsetTop);
    if (lastTop === null || Math.abs(top - lastTop) > 8) { row++; lastTop = top; }
    const tint = ROW_TINTS[row % ROW_TINTS.length];
    const media = c.querySelector('.product-card__media');
    media.style.setProperty('background', tint);
    media.dataset.tint = tint;
  });
}

function render() {
  /* own-property lookup only - a ?sort= of __proto__/toString resolves to an
     inherited non-comparator that the || fallback would not catch, and throws. */
  const cmp = Object.prototype.hasOwnProperty.call(SORTS, state.sort) ? SORTS[state.sort] : SORTS.featured;
  const items = ALL.filter(matches).sort(typeof cmp === 'function' ? cmp : SORTS.featured);
  renderGrid(grid, items);
  $('[data-count]').textContent = `${items.length} product${items.length === 1 ? '' : 's'}`;
  $('[data-empty]').hidden = items.length > 0;
  $('[data-chips]').innerHTML = chipsHTML();
  $('[data-rail]').innerHTML = railHTML();
  /* the select was never synced, so ?sort=price-asc sorted the grid while the control
     still displayed "Featured" and re-picking it fired no change event */
  const sortSel = $('[data-sort]');
  if (sortSel && sortSel.value !== state.sort) sortSel.value = state.sort;
  /* Keep the collection search box in step with state.q (a ?q= link, or the chip's ✕),
     but never overwrite it while the shopper is still typing. */
  const qInput = $('[data-q]');
  if (qInput && document.activeElement !== qInput && qInput.value !== state.q) qInput.value = state.q;
  requestAnimationFrame(tintRows);
  writeURL();
  updateHeading();
}

/* captured once, before any filter has touched the DOM */
let DEFAULT_TITLE, DEFAULT_SUB, DEFAULT_CRUMB;
function captureHeadingDefaults() {
  if (DEFAULT_TITLE !== undefined) return;
  DEFAULT_TITLE = $('[data-title]')?.textContent ?? 'The Collection';
  DEFAULT_SUB   = $('[data-subtitle]')?.textContent ?? '';
  DEFAULT_CRUMB = $('[data-crumb]')?.textContent ?? 'Shop';
}

function updateHeading() {
  captureHeadingDefaults();
  const t = $('[data-title]'), s = $('[data-subtitle]'), c = $('[data-crumb]');
  if (state.con.size === 1) {
    const k = [...state.con][0];
    t.textContent = CONCERN_LABEL[k]; c.textContent = 'Shop by Concern';
    s.textContent = 'Chosen by our estheticians for this concern. Start with one product, not five.';
  } else if (state.brand.size === 1) {
    t.textContent = [...state.brand][0]; c.textContent = 'Brand';
    s.textContent = 'A professional line we carry and use inside the spa.';
  } else if (state.cat.size === 1) {
    t.textContent = [...state.cat][0]; c.textContent = 'Category';
  } else if (state.best) { t.textContent = 'Best Sellers'; c.textContent = 'Spa Favorites'; s.textContent = DEFAULT_SUB; }
  else if (state.new) { t.textContent = 'New Arrivals'; c.textContent = 'Just In'; s.textContent = DEFAULT_SUB; }
  else {
    /* no branch reset the heading, so clearing a brand filter left the h1 reading
       "Environ" over all 258 products */
    t.textContent = DEFAULT_TITLE; c.textContent = DEFAULT_CRUMB; s.textContent = DEFAULT_SUB;
  }
}

document.addEventListener('change', e => {
  const f = e.target.closest('[data-facet]');
  if (f) { f.checked ? state[f.dataset.facet].add(f.value) : state[f.dataset.facet].delete(f.value); return render(); }
  const t = e.target.closest('[data-toggle]');
  if (t) { state[t.dataset.toggle] = t.checked; return render(); }
  const p = e.target.closest('[data-price]');
  if (p) {
    const v = p.value === '' ? null : Number(p.value);
    state[p.dataset.price] = Number.isFinite(v) ? v : null;
    const which = p.dataset.price;
    render();
    /* re-focus the rebuilt input so entering Min then Max actually works */
    const back = document.querySelector(`[data-price="${which}"]`);
    if (back) { back.focus(); back.selectionStart = back.selectionEnd = back.value.length; }
    return;
  }
  const s = e.target.closest('[data-sort]');
  if (s) { state.sort = s.value; return render(); }
});

document.addEventListener('click', e => {
  const rm = e.target.closest('[data-remove]');
  if (rm) {
    const { remove: kind, value } = rm.dataset;
    if (kind === 'price') { state.min = state.max = null; }
    else if (kind === 'toggle') { state[value] = false; }
    else if (kind === 'q') { state.q = ''; }
    else state[kind].delete(value);
    return render();
  }
  if (e.target.closest('[data-clear-all]')) {
    ['brand', 'cat', 'con'].forEach(k => state[k].clear());
    Object.assign(state, { min: null, max: null, inStock: false, best: false, new: false, q: '' });
    return render();
  }
  const g = e.target.closest('.filter-group__title');
  if (g) {
    const grp = g.closest('.filter-group');
    grp.classList.toggle('is-closed');
    g.setAttribute('aria-expanded', String(!grp.classList.contains('is-closed')));
  }
  if (e.target.closest('[data-filter-toggle]')) $('[data-rail]').classList.toggle('is-open');
  if (e.target.closest('[data-filter-close]') || e.target.closest('[data-filter-apply]')) $('[data-rail]').classList.remove('is-open');
});

/* Search inside the collection. The shop already filtered on ?q= (the header search's
   "See all results" link lands here) and showed it as a removable chip, but there was no box
   to type into, so a shopper browsing Toners could not narrow the grid by name. Uses the same
   tokenised searchScore as the header search, and combines with every other filter. */
let qTimer;
document.addEventListener('input', e => {
  const qi = e.target.closest('[data-q]');
  if (!qi) return;
  clearTimeout(qTimer);
  qTimer = setTimeout(() => { state.q = qi.value.trim(); render(); }, 160);
});

addEventListener('resize', () => requestAnimationFrame(tintRows));

(async () => {
  ALL = await catalog();
  readURL();
  render();
  /* #search opens the site's own overlay - never a native prompt(), which any
     third party could fire at a visitor just by sending them the link. */
  if (location.hash === '#search') document.querySelector('[data-search]')?.click();
  initMotion();
})();
